import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext, FormikHelpers, Field } from "formik";
import { Typography, Alert, Modal, Paper } from "@mui/material";
import { Tooltip } from "@mui/material";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import { ReactComponent as InformationIcon } from "../../assets/images/information-line.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as QRCodeIcon } from "../../assets/images/qr-code-line.svg";
import { ReactComponent as EyeIcon } from "../../assets/images/eye-line.svg";
import AddButton from "../../components/ui/AddButton";
import RoomLinkingModal from "../../components/modals/RoomLinkingModal";
import NavigationTags from "../../components/common/NavigationTags";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import { FileInput } from "../../components/common/FileInput";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { TextField } from "../../components/common/FormFields";
import HubAvailableLanguagesPicker from "../../components/common/HubAvailableLanguagesPicker";
import { getAllSingleTracks } from "../../api/singleTrack";
import { SingleTrackResponse } from "../../types/requestsOutputs";
import FormGroup from "../../components/common/FormGroup";
import { uploadFileToS3Complete } from "../../api/s3";
import {
    getSingleTrackFolders,
    updateSingleTrackFolder,
    deleteSingleTrackFolder,
    getSingleTrackFirstFilters,
} from "../../api/company";
import { generateQRCodeFromText } from "../../api/support";
import ButtonComponent from "../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { GetSingleTrackFoldersResponse } from "../../types/requestsOutputs";
import { useGlobalState } from "../../context/useGlobalState";
import { useNotification } from "../../context/NotificationContext";
import DeleteModal from "../../components/modals/DeleteModal";
import { SketchPicker, ColorResult } from "react-color";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import ImagePreview from "../../components/images/ImagePreview";
import { useImageValidation } from "../../hooks/useImageValidation";
import hubStyles from "./Hub.module.css";
import pageHeader from "../../components/layouts/PageHeader.module.css";
import studio from "../../components/modals/StudioModal.module.css";
import stepStyles from "../track/create/createStep.module.css";

interface HubFormValues {
    name: string;
    availableLanguagesTab: string[];
    logo: string | undefined;
    color: string;
}

type Hub = GetSingleTrackFoldersResponse[0];

function languageIdsUsedInHubContent(
    tracks: SingleTrackResponse[],
    languagesAI: Array<{ value: string; code: string }>,
): string[] {
    const codes = new Set<string>();
    for (const row of tracks) {
        const hasContent =
            (row.name && String(row.name).trim() !== "") ||
            (row.description && String(row.description).trim() !== "") ||
            (row.audiotrack?.file && String(row.audiotrack.file).trim() !== "");
        if (hasContent) {
            codes.add(String(row.language).trim().toUpperCase());
        }
    }
    return languagesAI.filter((l) => codes.has(String(l.code).trim().toUpperCase())).map((l) => l.value);
}

const HubForm = ({
    onClose,
    isEdit = false,
    originalHub,
    lockedLanguageIds = [],
}: {
    onClose: () => void;
    isEdit?: boolean;
    originalHub?: Hub | null;
    lockedLanguageIds?: string[];
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { values, setFieldValue, errors, touched, isSubmitting, submitForm } = useFormikContext<HubFormValues>();
    const { data, loading } = useGlobalState();
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const imageValidation = useImageValidation({
        type: "hubLogo",
        imageUrl: values.logo || undefined,
    });

    const handleLogoFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true,
                    isFolder: false,
                },
                token,
            )
                .then((result) => {
                    setFieldValue("logo", result);
                })
                .catch(console.error);
        }
    };

    const handleColorChange = (selectedColor: ColorResult) => {
        setFieldValue("color", selectedColor.hex);
    };

    const language = data.languagesAI.find((lang) => lang.value === originalHub?.originLanguageId)?.label;

    if (loading) {
        return (
            <div className="form-loading-container">
                <div className={hubStyles.formLoadingSpinner}></div>
                <p className="form-loading-text">{t("loadingLanguages")}</p>
            </div>
        );
    }

    return (
        <Form>
            <div className={hubStyles.hubFormContainer}>
                <h2 className={studio.dialogTitle}>{isEdit ? t("editHub") : t("createHub")}</h2>

                {/* Name */}
                <FormGroup
                    title={t("hubName")}
                    subtitle={t("nameWithOriginLanguage", { language: language })}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field name="name" component={TextField} placeholder={t("enterHubName")} />
                    {touched.name && errors.name && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.name}
                        </Alert>
                    )}
                </FormGroup>

                {/* Available Languages */}
                <FormGroup
                    title={t("availableLanguages")}
                    subtitle={t("availableLanguagesDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field name="availableLanguagesTab">
                        {({ field, form }: any) => (
                            <HubAvailableLanguagesPicker
                                selectedIds={field.value || []}
                                onChange={(ids) => {
                                    void form.setFieldValue(field.name, ids, true);
                                    void form.setFieldTouched(field.name, true, false);
                                }}
                                options={data.languagesAI}
                                lockedIds={isEdit ? lockedLanguageIds : []}
                            />
                        )}
                    </Field>
                    {touched.availableLanguagesTab && errors.availableLanguagesTab && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.availableLanguagesTab}
                        </Alert>
                    )}
                </FormGroup>

                {/* Logo */}
                <FormGroup
                    title={t("hubLogo")}
                    subtitle={t("hubLogoDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <ImageSpecsBadges spec="hubLogo" warnings={imageValidation.warnings} />
                    <div className={`${hubStyles.fileUpload} form-file-upload-constrained`}>
                        <FileInput
                            onFileSelected={handleLogoFileSelected}
                            showButton={true}
                            buttonVariant="outline"
                            placeholder={t("dragAndDropLogoHereOrClickToSelect")}
                            acceptedFileTypes={IMAGE_FORMAT}
                            maxSize={IMAGE_MAX_SIZE_200_KB}
                            maxSizeLabel={t("imageMaxSize200kb")}
                        />
                    </div>

                    {values.logo && (
                        <div className={hubStyles.logoPreviewContainer}>
                            <Typography variant="subtitle2" className={hubStyles.previewLabel}>
                                {t("logoPreview")}:
                            </Typography>
                            <ImagePreview
                                key={values.logo}
                                id={values.logo}
                                photo={values.logo}
                                onDelete={() => setFieldValue("logo", null)}
                            />
                        </div>
                    )}
                </FormGroup>

                {/* Color */}
                <FormGroup
                    title={t("hubColor")}
                    subtitle={t("hubColorDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <div className={hubStyles.colorInputContainer}>
                        <Field
                            name="color"
                            component={TextField}
                            placeholder={t("enterColorCode")}
                            className={hubStyles.colorInputField}
                        />
                        <div
                            className={hubStyles.colorPreview}
                            style={{ backgroundColor: values.color || "#000000" }}
                            onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                            title={t("clickToPickColor")}
                            role="button"
                            tabIndex={0}
                        >
                            <span>{values.color || "#000000"}</span>
                        </div>

                        {isColorPickerOpen && (
                            <div className="color-picker-popup">
                                <SketchPicker
                                    color={values.color || "#000000"}
                                    onChange={handleColorChange}
                                    disableAlpha={true}
                                />
                            </div>
                        )}
                    </div>

                    {touched.color && errors.color && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.color}
                        </Alert>
                    )}
                </FormGroup>

                {/* Action Buttons */}
                <div className={studio.dialogActions}>
                    <ButtonComponent
                        variant="cancel"
                        shape="pill"
                        label={t("cancel")}
                        onClick={onClose}
                        disabled={isSubmitting}
                    />
                    <Tooltip
                        title={
                            Object.keys(errors).length > 0
                                ? t("formIncompleteTooltip", { fields: Object.values(errors).join(", ") })
                                : ""
                        }
                        arrow
                    >
                        <span>
                            <ButtonComponent
                                variant="primary"
                                shape="pill"
                                label={isEdit ? t("updateHub") : t("createHub")}
                                onClick={submitForm}
                                disabled={isSubmitting || Object.keys(errors).length > 0}
                            />
                        </span>
                    </Tooltip>
                </div>
            </div>
        </Form>
    );
};

const HubList: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const { data } = useGlobalState();
    const { showNotification } = useNotification();
    const [hubs, setHubs] = useState<GetSingleTrackFoldersResponse>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingHub, setEditingHub] = useState<Hub | null>(null);
    const [selectedHubToDelete, setSelectedHubToDelete] = useState<Hub | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [, setIsQRCodeLoading] = useState(false);
    const [hubsWithActiveFolders, setHubsWithActiveFolders] = useState<Set<string>>(new Set());
    const [isCheckingFolders, setIsCheckingFolders] = useState(false);
    const [roomLinkingModal, setRoomLinkingModal] = useState<{
        open: boolean;
        folderId: string;
        folderName: string;
    }>({
        open: false,
        folderId: "",
        folderName: "",
    });
    const [lockedLanguageIds, setLockedLanguageIds] = useState<string[]>([]);

    // Helper function to convert language codes to IDs and vice versa
    const convertLanguageCodesToIds = (codes: string[]) => {
        if (!data.languagesAI.length || !codes.length) return [];
        return codes
            .map((code) => {
                const language = data.languagesAI.find((lang) => lang.code === code);
                return language ? language.value : code;
            })
            .filter(Boolean);
    };

    const convertLanguageIdsToCode = (ids: string[]) => {
        if (!data.languagesAI.length || !ids.length) return [];
        return ids
            .map((id) => {
                const language = data.languagesAI.find((lang) => lang.value === id);
                return language ? language.code : id;
            })
            .filter(Boolean);
    };

    // Stable validate fn so the edit modal Formik instance doesn’t reset validation oddly each parent render
    const validateForm = useCallback(
        (values: HubFormValues) => {
            const errors: Record<string, string> = {};

            if (!values.name || values.name.trim() === "") {
                errors.name = t("hubNameRequired");
            }

            if (!values.logo) {
                errors.logo = t("logoRequired");
            }

            const langs = values.availableLanguagesTab;
            if (!langs || langs.length === 0 || !langs.every((id) => id != null && String(id).trim() !== "")) {
                errors.availableLanguagesTab = t("atLeastOneLanguageRequired");
            }

            if (values.color && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(values.color)) {
                errors.color = t("invalidColorFormat");
            }

            return errors;
        },
        [t],
    );

    // Fetch hubs on component mount
    useEffect(() => {
        fetchHubs();
        // refetch when language changes only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // Check which hubs have active folders
    useEffect(() => {
        if (hubs.length > 0) {
            checkHubsForActiveFolders();
        }
        // run when hubs list changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubs]);

    const fetchHubs = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await getSingleTrackFolders({ token, data: { language: i18n.language || "en" } });
            if (response) {
                setHubs(response);
            }
        } catch (error) {
            console.error("Error fetching hubs:", error);
            showNotification({
                message: t("errorFetchingHubs"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    // Check which hubs have at least one active folder
    const checkHubsForActiveFolders = async () => {
        setIsCheckingFolders(true);
        const token = localStorage.getItem("token");
        if (!token) {
            setIsCheckingFolders(false);
            return;
        }

        const hubsWithActive = new Set<string>();

        try {
            // Check each hub for active folders
            await Promise.all(
                hubs.map(async (hub) => {
                    try {
                        const foldersResponse = await getSingleTrackFirstFilters({
                            token,
                            data: {
                                companySingleTrackFolderId: hub.id,
                                language: i18n.language || "en",
                            },
                        });

                        // Check if any folder is active
                        const hasActiveFolders = foldersResponse.some((folder) => folder.status === true);
                        if (hasActiveFolders) {
                            hubsWithActive.add(hub.id);
                        }
                    } catch (error) {
                        console.error(`Error checking folders for hub ${hub.id}:`, error);
                    }
                }),
            );
        } catch (error) {
            console.error("Error checking hubs for active folders:", error);
        } finally {
            setHubsWithActiveFolders(hubsWithActive);
            setIsCheckingFolders(false);
        }
    };

    // Helper function to determine if view hub should be enabled
    const isViewHubEnabled = (hub: Hub) => {
        return hub.status && hubsWithActiveFolders.has(hub.id);
    };

    const handleCloseRoomLinking = () => {
        setRoomLinkingModal({
            open: false,
            folderId: "",
            folderName: "",
        });
    };

    const handleUpdateHub = async (values: HubFormValues, formikHelpers: FormikHelpers<HubFormValues>) => {
        if (!editingHub) return;

        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        try {
            // Build update data with only changed fields
            const updateData: any = { folderId: editingHub.id };

            // Only include fields that have changed
            if (values.name !== editingHub.internalName) {
                updateData.newName = values.name;
            }
            if (values.logo !== (editingHub.logo || "")) {
                updateData.logo = values.logo;
            }
            if (values.color !== (editingHub.color || "#000000")) {
                updateData.color = values.color;
            }

            // Convert and compare availableLanguagesTab
            const currentLanguageCodes = editingHub.availableLanguagesTab || [];
            const newLanguageCodes = convertLanguageIdsToCode(values.availableLanguagesTab);
            if (JSON.stringify(currentLanguageCodes.sort()) !== JSON.stringify(newLanguageCodes.sort())) {
                updateData.availableLanguagesTab = newLanguageCodes;
            }

            await updateSingleTrackFolder({ token, data: updateData });
            showNotification({
                message: t("hubUpdatedSuccessfully"),
                type: "success",
            });
            setModalOpen(false);
            setEditingHub(null);
            fetchHubs();
            formikHelpers.resetForm();
        } catch (error: any) {
            console.error("Error updating hub:", error);
            const apiMessage = error?.message || error?.details?.message;
            showNotification({
                message: apiMessage && String(apiMessage).trim() !== "" ? apiMessage : t("errorUpdatingHub"),
                type: "error",
            });
        }
    };

    const handleDeleteHub = async (hubId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        // Store the hub being deleted for potential revert
        const hubToDelete = hubs.find((h) => h.id === hubId);
        if (!hubToDelete) return;

        // Optimistic update: immediately remove from UI
        setHubs((prevHubs) => prevHubs.filter((h) => h.id !== hubId));

        try {
            await deleteSingleTrackFolder({
                token,
                data: { folderId: hubId },
            });
            showNotification({
                message: t("hubDeletedSuccessfully"),
                type: "success",
            });
        } catch (error) {
            console.error("Error deleting hub:", error);
            showNotification({
                message: t("errorDeletingHub"),
                type: "error",
            });

            // Revert the optimistic update on error by adding the hub back
            setHubs((prevHubs) => [...prevHubs, hubToDelete]);
        }
    };

    const openEditModal = async (hub: Hub) => {
        setEditingHub(hub);
        setLockedLanguageIds([]);
        setModalOpen(true);
        const token = localStorage.getItem("token");
        if (!token || !data.languagesAI.length) return;
        try {
            const tracks = await getAllSingleTracks({
                token,
                data: { folderId: hub.id },
            });
            setLockedLanguageIds(languageIdsUsedInHubContent(tracks, data.languagesAI));
        } catch (e) {
            console.error("Error loading hub story languages:", e);
        }
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingHub(null);
        setLockedLanguageIds([]);
    };

    const handleConfigureHub = (hub: Hub) => {
        navigate(`/hubs/folders?hubId=${hub.id}`);
    };

    const handleQRCodeGeneration = async (OTPToken: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        try {
            const hubUrl = `hub.rewindstories.com/?hub=${OTPToken}`;
            setIsQRCodeLoading(true);
            const response = await generateQRCodeFromText(token, hubUrl);
            setIsQRCodeLoading(false);
            window.open(response.url, "_blank");
        } catch (error) {
            console.error("Failed to generate QR code:", error);
            showNotification({
                message: t("errorGeneratingQRCode"),
                type: "error",
            });
        }
    };

    const handleViewHub = (OTPToken: string) => {
        const hubUrl = `https://hub.rewindstories.com/?hub=${OTPToken}`;
        window.open(hubUrl, "_blank");
    };

    const handleToggleStatus = async (hub: Hub) => {
        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        // Optimistic update: immediately update the UI
        const newStatus = !hub.status;
        setHubs((prevHubs) => prevHubs.map((h) => (h.id === hub.id ? { ...h, status: newStatus } : h)));

        try {
            await updateSingleTrackFolder({
                token,
                data: {
                    folderId: hub.id,
                    status: newStatus,
                },
            });
            // No need to refetch since we already updated optimistically
            // But we need to recheck folder status since hub status changed
            checkHubsForActiveFolders();
        } catch (error) {
            console.error("Error updating hub status:", error);
            showNotification({
                message: t("errorUpdatingHubStatus"),
                type: "error",
            });

            // Revert the optimistic update on error
            setHubs((prevHubs) => prevHubs.map((h) => (h.id === hub.id ? { ...h, status: hub.status } : h)));
        }
    };

    // Initial values for forms
    const initialValues: HubFormValues = useMemo(
        () => ({
            name: editingHub?.internalName || "",
            availableLanguagesTab: editingHub?.availableLanguagesTab
                ? convertLanguageCodesToIds(editingHub.availableLanguagesTab)
                : [],
            logo: editingHub?.logo || undefined,
            color: editingHub?.color || "#000000",
        }),
        // convertLanguageCodesToIds depends on data.languagesAI (already listed)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editingHub, data.languagesAI],
    );

    return (
        <div className={`${hubStyles.root} ${hubStyles.page}`}>
            <div className={`${hubStyles.pageHeader} ${hubStyles.head}`}>
                <div className={hubStyles.crumbTop}>{t("myHubs")}</div>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className={hubStyles.settingsTitle}>{t("myHubsTitle")}</h2>
                        <div className={hubStyles.pageSubtitleWithInfo}>
                            <Typography variant="body2" className={hubStyles.pageSubtitle}>
                                {t("myHubsDescription")}
                            </Typography>
                            <Tooltip title={t("hubExplanationTooltip")} arrow placement="top">
                                <InformationIcon className={hubStyles.infoIcon} />
                            </Tooltip>
                        </div>
                    </div>
                    <AddButton
                        labeledClassName={hubStyles.addButtonLabeled}
                        onClick={() => navigate("/hubs/create")}
                        tooltip={t("createHub")}
                        label={t("newHub")}
                    />
                </div>
                <NavigationTags
                    classes={{
                        container: hubStyles.navigationTags,
                        tag: hubStyles.navigationTag,
                        tagActive: hubStyles.navigationTagActive,
                        tagDisabled: hubStyles.navigationTagDisabled,
                        arrow: hubStyles.navigationTagArrow,
                    }}
                    tags={[
                        { label: t("hub"), isActive: true },
                        { label: t("page") },
                        { label: t("category") },
                        { label: t("content") },
                    ]}
                />
            </div>

            {loading ? (
                <div className="form-loading-container">
                    <div className={hubStyles.formLoadingSpinner}></div>
                    <p className="form-loading-text">{t("loadingHubs")}</p>
                </div>
            ) : hubs.length === 0 ? (
                <div className={hubStyles.emptyState}>
                    <div className={hubStyles.emptyStateIcon}>🌐</div>
                    <Typography variant="h6" className={hubStyles.emptyStateTitle}>
                        {t("noHubsYet")}
                    </Typography>
                    <Typography variant="body2" className={hubStyles.emptyStateText}>
                        {t("noHubsDescription")}
                    </Typography>
                    <AddButton
                        labeledClassName={hubStyles.emptyStateButton}
                        onClick={() => navigate("/hubs/create")}
                        tooltip={t("createHub")}
                        label={t("newHub")}
                    />
                </div>
            ) : (
                <div className={hubStyles.grid}>
                    {hubs.map((hub) => (
                        <div key={hub.id} className={hubStyles.hubCard} onClick={() => handleConfigureHub(hub)}>
                            <div className={hubStyles.hubCardContent}>
                                {/* Color dot */}
                                <div
                                    className={hubStyles.hubColorDot}
                                    style={{ backgroundColor: hub.color || "#9e9e9e" }}
                                />

                                {/* Hub name */}
                                <span className={hubStyles.hubRowName}>{hub.internalName}</span>

                                {/* Status toggle */}
                                <div
                                    className={hubStyles.statusToggleContainer}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    <span
                                        className={`${hubStyles.statusLabel}${hub.status ? ` ${hubStyles.statusLabelActive}` : ""}`}
                                    >
                                        {hub.status ? t("active") : t("inactive")}
                                    </span>
                                    <ToggleSwitch
                                        checked={(hub as Hub).status || false}
                                        onChange={() => handleToggleStatus(hub as Hub)}
                                    />
                                </div>

                                {/* Vertical separator */}
                                <div className={hubStyles.rowSeparator} />

                                {/* Actions */}
                                <div
                                    className={hubStyles.hubCardActions}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    {hub?.oTPToken?.[0]?.token && (
                                        <Tooltip
                                            title={
                                                isCheckingFolders
                                                    ? t("checkingFolders")
                                                    : isViewHubEnabled(hub)
                                                      ? t("viewHub")
                                                      : t("hubNotActiveOrNoActiveFolders")
                                            }
                                        >
                                            <span>
                                                <EyeIcon
                                                    className={hubStyles.mutedIconButton}
                                                    style={{
                                                        opacity: isViewHubEnabled(hub) && !isCheckingFolders ? 1 : 0.35,
                                                        cursor:
                                                            isViewHubEnabled(hub) && !isCheckingFolders
                                                                ? "pointer"
                                                                : "not-allowed",
                                                    }}
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        if (isViewHubEnabled(hub) && !isCheckingFolders) {
                                                            const token = hub?.oTPToken?.[0]?.token;
                                                            if (token) handleViewHub(token);
                                                        }
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {hub?.oTPToken?.[0]?.token && (
                                        <Tooltip title={t("downloadQRCode")}>
                                            <QRCodeIcon
                                                className={hubStyles.mutedIconButton}
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    const token = hub?.oTPToken?.[0]?.token;
                                                    if (token) handleQRCodeGeneration(token);
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip title={t("edit")} arrow>
                                        <EditIcon
                                            className={hubStyles.hubSettingsIconButton}
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                openEditModal(hub);
                                            }}
                                        />
                                    </Tooltip>
                                    <Tooltip title={t("delete")} arrow>
                                        <DeleteIcon
                                            className={hubStyles.hubDeleteIconButton}
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                setSelectedHubToDelete(hub);
                                                setIsDeleteModalOpen(true);
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {selectedHubToDelete && (
                <DeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => {
                        setIsDeleteModalOpen(false);
                        setSelectedHubToDelete(null);
                    }}
                    onConfirm={() => {
                        handleDeleteHub(selectedHubToDelete.id);
                        setSelectedHubToDelete(null);
                    }}
                    message={t("deleteHubConfirmation")}
                    title={t("deleteHub")}
                />
            )}

            {/* Edit Modal */}
            <Modal open={modalOpen} onClose={closeModal} className={hubStyles.hubModalOverlay}>
                <Paper className={hubStyles.hubModalContent}>
                    <Formik
                        initialValues={
                            editingHub
                                ? {
                                      name: editingHub.internalName,
                                      availableLanguagesTab: editingHub.availableLanguagesTab
                                          ? convertLanguageCodesToIds(editingHub.availableLanguagesTab)
                                          : [],
                                      logo: editingHub.logo || undefined,
                                      color: editingHub.color || "#000000",
                                  }
                                : initialValues
                        }
                        validate={validateForm}
                        onSubmit={handleUpdateHub}
                        enableReinitialize
                        validateOnMount
                        validateOnChange
                    >
                        <HubForm
                            onClose={closeModal}
                            isEdit={!!editingHub}
                            originalHub={editingHub}
                            lockedLanguageIds={lockedLanguageIds}
                        />
                    </Formik>
                </Paper>
            </Modal>

            {/* Room Linking Modal */}
            <RoomLinkingModal
                open={roomLinkingModal.open}
                onClose={handleCloseRoomLinking}
                folderId={roomLinkingModal.folderId}
                folderName={roomLinkingModal.folderName}
                onSuccess={() => {
                    // Optionally refresh data or show success message
                    fetchHubs();
                }}
            />
        </div>
    );
};

export default HubList;
